import React, { useState, useEffect } from 'react';
import { User, Upload, Save, Camera, Phone, Mail, FileText, Settings as SettingsIcon, MessageCircle, Lock, Key } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface UserProfile {
  id: string;
  telegram_id: number;
  telegram_username: string | null;
  first_name: string | null;
  last_name: string | null;
  profile_photo_url: string | null;
  bio: string | null;
  phone_number: string | null;
  preferences: {
    theme?: string;
    notifications_enabled?: boolean;
    language?: string;
  };
  role: string;
  business_id: string | null;
}

interface ProfileManagementProps {
  userId: string;
  onClose?: () => void;
}

export default function ProfileManagement({ userId, onClose }: ProfileManagementProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    phone_number: '',
    telegram_username: '',
    theme: 'light',
    notifications_enabled: true,
    language: 'en',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      console.log('Loading profile for userId:', userId);

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (userError) {
        console.error('Error loading profile:', userError);
        throw userError;
      }

      console.log('User profile data:', userData);

      if (userData) {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('*')
          .eq('telegram_id', userData.telegram_id)
          .maybeSingle();

        console.log('Admin profile data:', adminData);

        if (adminData) {
          setProfile({
            id: userData.id,
            telegram_id: userData.telegram_id,
            telegram_username: userData.telegram_username || null,
            first_name: userData.first_name || adminData.full_name?.split(' ')[0] || '',
            last_name: userData.last_name || adminData.full_name?.split(' ').slice(1).join(' ') || '',
            profile_photo_url: userData.profile_photo_url,
            bio: userData.bio,
            phone_number: userData.phone_number,
            preferences: userData.preferences || {},
            role: adminData.role,
            business_id: userData.business_id,
          });
          setFormData({
            first_name: userData.first_name || adminData.full_name?.split(' ')[0] || '',
            last_name: userData.last_name || adminData.full_name?.split(' ').slice(1).join(' ') || '',
            bio: userData.bio || '',
            phone_number: userData.phone_number || '',
            telegram_username: userData.telegram_username || adminData.telegram_username || '',
            theme: userData.preferences?.theme || 'light',
            notifications_enabled: userData.preferences?.notifications_enabled ?? true,
            language: userData.preferences?.language || 'en',
            email: adminData.email || '',
            current_password: '',
            new_password: '',
            confirm_password: '',
          });
        } else {
          setProfile(userData);
          setFormData({
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            bio: userData.bio || '',
            phone_number: userData.phone_number || '',
            telegram_username: userData.telegram_username || '',
            theme: userData.preferences?.theme || 'light',
            notifications_enabled: userData.preferences?.notifications_enabled ?? true,
            language: userData.preferences?.language || 'en',
            email: '',
            current_password: '',
            new_password: '',
            confirm_password: '',
          });
        }
      }
    } catch (err: any) {
      console.error('Profile load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, file, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('users')
        .update({ profile_photo_url: publicUrl })
        .eq('id', userId);

      if (updateError) throw updateError;

      setProfile(prev => prev ? { ...prev, profile_photo_url: publicUrl } : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (formData.new_password && formData.new_password !== formData.confirm_password) {
        throw new Error('New passwords do not match');
      }

      if (formData.new_password && formData.new_password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const userUpdateData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        bio: formData.bio,
        phone_number: formData.phone_number,
        telegram_username: formData.telegram_username,
        preferences: {
          theme: formData.theme,
          notifications_enabled: formData.notifications_enabled,
          language: formData.language,
        },
        updated_at: new Date().toISOString(),
      };

      const { error: userError } = await supabase
        .from('users')
        .update(userUpdateData)
        .eq('id', userId);

      if (userError) throw userError;

      if (profile?.role === 'super_admin' || profile?.role === 'team_leader') {
        const fullName = `${formData.first_name} ${formData.last_name}`.trim();
        const adminUpdateData: any = {
          full_name: fullName,
          telegram_username: formData.telegram_username,
          updated_at: new Date().toISOString(),
        };

        if (formData.email) {
          adminUpdateData.email = formData.email;
        }

        if (formData.new_password && formData.current_password) {
          const { data: verifyData } = await supabase.rpc('verify_admin_password', {
            p_admin_id: profile.telegram_id.toString(),
            p_password: formData.current_password
          });

          if (!verifyData) {
            throw new Error('Current password is incorrect');
          }

          adminUpdateData.password_hash = formData.new_password;
        }

        const { error: adminError } = await supabase
          .from('admin_users')
          .update(adminUpdateData)
          .eq('telegram_id', profile.telegram_id);

        if (adminError) throw adminError;
      }

      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        confirm_password: '',
      });

      await loadProfile();
      if (onClose) onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Profile Settings</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              ✕
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <div className="relative">
                {profile?.profile_photo_url ? (
                  <img
                    src={profile.profile_photo_url}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover border-4 border-brand-primary/20"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-brand-primary/10 flex items-center justify-center border-4 border-brand-primary/20">
                    <User className="w-12 h-12 text-brand-primary" />
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-brand-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-brand-secondary transition-colors shadow-brand">
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
              {uploading && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">Uploading...</p>
              )}
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {formData.first_name} {formData.last_name}
              </h3>
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-4 h-4 text-blue-500" />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  @{profile?.telegram_username || 'No username'}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="badge badge-info">
                  {profile?.role === 'team_leader' ? 'Team Leader' : 'User'}
                </span>
                <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded">
                  Telegram ID: {profile?.telegram_id}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                First Name
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="input-field"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last Name
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="input-field"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <MessageCircle className="w-4 h-4 inline mr-2" />
              Telegram Username
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-400">
                @
              </span>
              <input
                type="text"
                value={formData.telegram_username}
                onChange={(e) => setFormData({ ...formData, telegram_username: e.target.value })}
                className="input-field pl-8"
                placeholder="yourusername"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Your Telegram username (without the @ symbol)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              className="input-field"
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Bio
            </label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              className="input-field"
              rows={4}
              placeholder="Tell us about yourself..."
            />
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              Preferences
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Theme
                </label>
                <select
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="input-field"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="input-field"
                >
                  <option value="en">English</option>
                  <option value="ms">Bahasa Melayu</option>
                  <option value="zh">中文</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notifications_enabled}
                  onChange={(e) => setFormData({ ...formData, notifications_enabled: e.target.checked })}
                  className="w-4 h-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Telegram notifications
                </span>
              </label>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Security Settings
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                  placeholder="your.email@example.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave blank to keep current email
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Change Password
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={formData.current_password}
                      onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                      className="input-field"
                      placeholder="Enter current password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={formData.new_password}
                      onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                      className="input-field"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                      className="input-field"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Leave password fields blank to keep current password
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {onClose && (
              <button
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={saving}
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSave}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
